import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { activeLeagueManagementMembership } from "@/models/user";
import {
  useAIChatController,
  type AIChatController,
  type AIChatNavigationHost,
} from "./hooks/useAIChatController";
import {
  SBotAppEnvironmentContextStore,
  type SBotAppEnvironmentContextState,
  type SBotAppEnvironmentSurfaceReport,
} from "./services/appEnvironmentContext";

interface AIChatContextValue {
  controller: AIChatController;
  navigationHost: AIChatNavigationHost;
  floatingAutoOpenRequestID: number | null;
  requestFloatingAutoOpen: () => void;
  acknowledgeFloatingAutoOpen: (requestID: number) => void;
  openInternalAppLink: (appPath: string) => void;
}

const AIChatContext = createContext<AIChatContextValue | null>(null);
const AppEnvironmentContext = createContext<SBotAppEnvironmentContextStore | null>(null);
const subscribeToNothing = (): (() => void) => () => undefined;
const noAppEnvironmentScopeGeneration = (): number => 0;

export function AIChatProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const {
    currentUser,
    isSBotLabsAdmin,
    isSBotAdminModeEnabled,
  } = useAuth();
  const [appEnvironmentContext] = useState(() => new SBotAppEnvironmentContextStore());
  const nextFloatingOpenRequestID = useRef(0);
  const [floatingAutoOpenRequestID, setFloatingAutoOpenRequestID] = useState<number | null>(null);
  const pathnameRef = useRef(pathname);
  const navigateRef = useRef(navigate);
  const pendingOpenRef = useRef<{
    appPath: string;
    resolve: (opened: boolean) => void;
    timeoutID: number;
  } | null>(null);

  const requestFloatingAutoOpen = useCallback(() => {
    nextFloatingOpenRequestID.current += 1;
    setFloatingAutoOpenRequestID(nextFloatingOpenRequestID.current);
  }, []);

  const acknowledgeFloatingAutoOpen = useCallback((requestID: number) => {
    setFloatingAutoOpenRequestID((current) => current === requestID ? null : current);
  }, []);

  useEffect(() => {
    pathnameRef.current = pathname;
    navigateRef.current = navigate;
    const pending = pendingOpenRef.current;
    if (pending && pathname === pending.appPath) {
      window.clearTimeout(pending.timeoutID);
      pendingOpenRef.current = null;
      pending.resolve(true);
    }
  }, [pathname, navigate]);

  useEffect(() => () => {
    const pending = pendingOpenRef.current;
    if (pending) {
      window.clearTimeout(pending.timeoutID);
      pending.resolve(false);
      pendingOpenRef.current = null;
    }
  }, []);

  const requestHandoffIfNeeded = useCallback((appPath: string) => {
    if (pathnameRef.current === "/ai" && appPath !== "/ai") {
      requestFloatingAutoOpen();
    }
  }, [requestFloatingAutoOpen]);

  const openAppPath = useCallback((appPath: string): Promise<boolean> => {
    if (!appPath.startsWith("/")) return Promise.resolve(false);
    if (pathnameRef.current === appPath) return Promise.resolve(true);

    requestHandoffIfNeeded(appPath);
    const previous = pendingOpenRef.current;
    if (previous) {
      window.clearTimeout(previous.timeoutID);
      previous.resolve(false);
    }

    return new Promise<boolean>((resolve) => {
      const timeoutID = window.setTimeout(() => {
        if (pendingOpenRef.current?.resolve === resolve) {
          pendingOpenRef.current = null;
          resolve(false);
        }
      }, 5_000);
      pendingOpenRef.current = { appPath, resolve, timeoutID };
      navigateRef.current(appPath);
    });
  }, [requestHandoffIfNeeded]);

  const openInternalAppLink = useCallback((appPath: string) => {
    if (!appPath.startsWith("/")) return;
    requestHandoffIfNeeded(appPath);
    navigateRef.current(appPath);
  }, [requestHandoffIfNeeded]);

  const navigationHost = useMemo<AIChatNavigationHost>(
    () => ({
      openAppPath,
      origin: () => window.location.origin,
    }),
    [openAppPath],
  );
  const controller = useAIChatController(navigationHost, appEnvironmentContext);
  const stopController = controller.stop;

  const selectedTab = selectedAppEnvironmentTab(pathname);
  const activeContext = currentUser?.activeContext;
  const canShowLeagueTab = isSBotLabsAdmin
    || (currentUser ? activeLeagueManagementMembership(currentUser) !== undefined : false);
  const scopeIdentity = currentUser && activeContext
    ? [
        currentUser.id,
        activeContext.targetType,
        activeContext.targetID,
        activeContext.role,
        isSBotLabsAdmin ? "labs-admin" : "member",
        isSBotAdminModeEnabled ? "admin-mode" : "workspace-mode",
        "ai-enabled",
      ].join("|")
    : undefined;
  const fallbackReport = useMemo(
    () => shellFallbackReport(pathname, selectedTab, canShowLeagueTab, isSBotAdminModeEnabled),
    [pathname, selectedTab, canShowLeagueTab, isSBotAdminModeEnabled],
  );
  const previousScopeIdentity = useRef<string | undefined>(undefined);

  useLayoutEffect(() => {
    const previous = previousScopeIdentity.current;
    if (previous !== undefined && previous !== scopeIdentity) stopController();
    previousScopeIdentity.current = scopeIdentity;
    appEnvironmentContext.updateShell({
      selectedTab,
      presentationStyle: pathname === "/ai" ? "dedicated" : "floating",
      scopeIdentity,
      workspace: activeContext
        ? {
            type: activeContext.targetType,
            displayName: activeContext.displayName,
            role: activeContext.role,
          }
        : undefined,
      fallbackReport,
    });
  }, [
    activeContext,
    appEnvironmentContext,
    fallbackReport,
    pathname,
    scopeIdentity,
    selectedTab,
    stopController,
  ]);

  useEffect(() => () => {
    stopController();
    appEnvironmentContext.clear();
  }, [appEnvironmentContext, stopController]);

  const value = useMemo<AIChatContextValue>(
    () => ({
      controller,
      navigationHost,
      floatingAutoOpenRequestID,
      requestFloatingAutoOpen,
      acknowledgeFloatingAutoOpen,
      openInternalAppLink,
    }),
    [
      controller,
      navigationHost,
      floatingAutoOpenRequestID,
      requestFloatingAutoOpen,
      acknowledgeFloatingAutoOpen,
      openInternalAppLink,
    ],
  );

  return (
    <AppEnvironmentContext.Provider value={appEnvironmentContext}>
      <AIChatContext.Provider value={value}>{children}</AIChatContext.Provider>
    </AppEnvironmentContext.Provider>
  );
}

export function useAppEnvironmentContextStore(): SBotAppEnvironmentContextStore | null {
  return useContext(AppEnvironmentContext);
}

export function useAppEnvironmentScopeGeneration(): number {
  const store = useContext(AppEnvironmentContext);
  return useSyncExternalStore(
    store?.subscribe ?? subscribeToNothing,
    store ? () => store.getState().scopeGeneration : noAppEnvironmentScopeGeneration,
    store ? () => store.getState().scopeGeneration : noAppEnvironmentScopeGeneration,
  );
}

export interface AppEnvironmentContextValue {
  snapshot: SBotAppEnvironmentContextState["currentSnapshot"] | null;
  sharesByDefault: boolean;
  setSharesByDefault: (shares: boolean) => void;
  isExcludedForNextInteraction: boolean;
  isContextAvailable: boolean;
  excludeNextInteraction: () => void;
  includeNextInteraction: () => void;
}

export function useAppEnvironmentContext(): AppEnvironmentContextValue {
  const store = useContext(AppEnvironmentContext);
  if (!store) throw new Error("App environment context must be used within an AIChatProvider.");
  const state = useSyncExternalStore(store.subscribe, store.getState, store.getState);
  return useMemo(
    () => ({
      snapshot: state.currentSnapshot ?? null,
      sharesByDefault: state.sharesByDefault,
      setSharesByDefault: (shares: boolean) => store.setSharesByDefault(shares),
      isExcludedForNextInteraction: state.isExcludedForNextInteraction,
      isContextAvailable: state.isContextAvailable,
      excludeNextInteraction: () => store.excludeNextInteraction(),
      includeNextInteraction: () => store.includeNextInteraction(),
    }),
    [state, store],
  );
}

export function useSharedAIChatController(): AIChatController {
  return useAIChatContext().controller;
}

/** The router-backed host the AI surfaces use to open in-app destinations. */
export function useAIChatNavigationHost(): AIChatNavigationHost {
  return useAIChatContext().navigationHost;
}

export function useAIChatHandoff(): Pick<
  AIChatContextValue,
  "floatingAutoOpenRequestID" | "requestFloatingAutoOpen" | "acknowledgeFloatingAutoOpen" | "openInternalAppLink"
> {
  const {
    floatingAutoOpenRequestID,
    requestFloatingAutoOpen,
    acknowledgeFloatingAutoOpen,
    openInternalAppLink,
  } = useAIChatContext();
  return {
    floatingAutoOpenRequestID,
    requestFloatingAutoOpen,
    acknowledgeFloatingAutoOpen,
    openInternalAppLink,
  };
}

function useAIChatContext(): AIChatContextValue {
  const value = useContext(AIChatContext);
  if (!value) {
    throw new Error("AI chat must be used within an AIChatProvider.");
  }
  return value;
}

function selectedAppEnvironmentTab(pathname: string): string {
  if (pathname === "/ai") return "ai";
  if (pathname === "/admin" || pathname.startsWith("/admin/")) return "admin";
  if (pathname === "/league" || pathname.startsWith("/league/")) return "league";
  if (pathname === "/account" || pathname.startsWith("/account/")) return "account";
  return "home";
}

function shellFallbackReport(
  pathname: string,
  selectedTab: string,
  canShowLeagueTab: boolean,
  isSBotAdminModeEnabled: boolean,
): SBotAppEnvironmentSurfaceReport | undefined {
  if (selectedTab === "ai") return undefined;
  const tabs = [
    { id: "home", label: "Home" },
    { id: "ai", label: "AI" },
    ...(canShowLeagueTab
      ? [{ id: isSBotAdminModeEnabled ? "admin" : "league", label: isSBotAdminModeEnabled ? "Admin" : "League" }]
      : []),
    { id: "account", label: "Account" },
  ];
  const title = tabs.find((tab) => tab.id === selectedTab)?.label
    ?? (selectedTab === "admin" ? "Admin" : "Home");
  return {
    identity: `tab:${selectedTab}`,
    owningTab: selectedTab,
    breadcrumbs: [title],
    destinationType: "tabRoot",
    surfaceLevel: "tabRoot",
    surfaceDepth: 0,
    surfacePresentation: isTabRootPath(pathname) ? "tabRoot" : "routeFallback",
    screen: {
      title,
      state: "visible",
      contextCompleteness: "basic",
      sections: [{
        id: "app-navigation",
        title: "App Navigation",
        controls: tabs.map((tab) => ({
          id: `main-tab-${tab.id}`,
          role: "button",
          label: tab.label,
          isEnabled: true,
          isSelected: tab.id === selectedTab,
        })),
      }],
    },
  };
}

function isTabRootPath(pathname: string): boolean {
  return pathname === "/home"
    || pathname === "/league"
    || pathname === "/account"
    || pathname === "/admin";
}
